import { Globe, ShieldCheck, Zap, Activity } from "lucide-react";
import { useScrollReveal, staggerStyle } from "@/hooks/useScrollReveal";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";

/**
 * Corridor map: Europe + the whole of Africa, drawn the same way on both
 * sides — city dots and routes, no country highlighting. Most links land
 * on the live corridors (Morocco, Algeria, Tunisia); a few reach further
 * south to show the network is growing. Accurate Mercator projection.
 */

const LON_MIN = -19;
const LON_MAX = 24;
const LAT_MIN = 2;
const LAT_MAX = 60;
const W = 800;
const merc = (lat: number) => Math.log(Math.tan(Math.PI / 4 + (lat * Math.PI) / 360));
const M_MIN = merc(LAT_MIN);
const M_MAX = merc(LAT_MAX);
const H = Math.round(W * ((M_MAX - M_MIN) / (((LON_MAX - LON_MIN) * Math.PI) / 180)));

function project(lon: number, lat: number): [number, number] {
  const x = ((lon - LON_MIN) / (LON_MAX - LON_MIN)) * W;
  const y = H - ((merc(Math.max(-85, Math.min(85, lat))) - M_MIN) / (M_MAX - M_MIN)) * H;
  return [x, y];
}

function ringToPath(ring: number[][]): string {
  return (
    ring
      .map((c, i) => {
        const [x, y] = project(c[0], c[1]);
        return `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join("") + "Z"
  );
}

function ringInFrame(ring: number[][]): boolean {
  return ring.some(([lon, lat]) => lon > LON_MIN - 6 && lon < LON_MAX + 6 && lat > LAT_MIN - 6 && lat < LAT_MAX + 6);
}

type Region = "europe" | "other";
interface Shape { d: string; region: Region }

const EUROPE_COUNTRIES = new Set([
  "France", "Belgium", "Netherlands", "Germany", "Spain", "Italy", "United Kingdom", "Ireland",
  "Sweden", "Norway", "Denmark", "Portugal", "Austria", "Poland", "Finland", "Switzerland", "Luxembourg",
]);

interface City { name: string; lon: number; lat: number; hub?: boolean; small?: boolean; anchor?: "start" | "end" | "middle"; dy?: number }

const cities: City[] = [
  { name: "London", lon: -0.12, lat: 51.51, anchor: "end" },
  { name: "Amsterdam", lon: 4.9, lat: 52.37, anchor: "start" },
  { name: "Brussels", lon: 4.35, lat: 50.85, anchor: "start" },
  { name: "Paris", lon: 2.35, lat: 48.86, anchor: "end" },
  { name: "Berlin", lon: 13.4, lat: 52.52 },
  { name: "Copenhagen", lon: 12.57, lat: 55.68 },
  { name: "Stockholm", lon: 18.07, lat: 59.33 },
  { name: "Madrid", lon: -3.7, lat: 40.42 },
  { name: "Barcelona", lon: 2.17, lat: 41.39, anchor: "start" },
  { name: "Marseille", lon: 5.37, lat: 43.3, anchor: "start" },
  { name: "Milan", lon: 9.19, lat: 45.46, anchor: "start" },
  { name: "Rotterdam", lon: 4.48, lat: 51.92, small: true },
  { name: "Hamburg", lon: 9.99, lat: 53.55, small: true },
  { name: "Frankfurt", lon: 8.68, lat: 50.11, small: true },
  { name: "Munich", lon: 11.58, lat: 48.14, small: true },
  { name: "Lyon", lon: 4.84, lat: 45.76, small: true },
  { name: "Zurich", lon: 8.54, lat: 47.37, small: true },
  { name: "Rome", lon: 12.5, lat: 41.9, small: true },
  { name: "Lisbon", lon: -9.14, lat: 38.72, small: true },
  { name: "Bordeaux", lon: -0.58, lat: 44.84, small: true },
  { name: "Oslo", lon: 10.75, lat: 59.91, small: true },
  { name: "Manchester", lon: -2.24, lat: 53.48, small: true },
  { name: "Malaga", lon: -4.42, lat: 36.72, small: true },
  { name: "Marrakech", lon: -7.99, lat: 31.63, hub: true },
  { name: "Agadir", lon: -9.6, lat: 30.43, hub: true },
  { name: "Fes", lon: -5.0, lat: 34.03, hub: true },
  { name: "Nador", lon: -2.93, lat: 35.17, hub: true },
  { name: "Oran", lon: -0.63, lat: 35.7, hub: true },
  { name: "Sfax", lon: 10.76, lat: 34.74, hub: true },
  { name: "Tangier", lon: -5.81, lat: 35.77, hub: true, anchor: "end" },
  { name: "Casablanca", lon: -7.59, lat: 33.57, hub: true, anchor: "end" },
  { name: "Algiers", lon: 3.06, lat: 36.75, hub: true, dy: 34 },
  { name: "Tunis", lon: 10.18, lat: 36.81, hub: true, anchor: "start" },
  { name: "Dakar", lon: -17.45, lat: 14.69, hub: true },
  { name: "Abidjan", lon: -4.01, lat: 5.35, hub: true },
  { name: "Douala", lon: 9.7, lat: 4.05, hub: true },
  { name: "Rabat", lon: -6.84, lat: 34.02, hub: true },
  { name: "Meknes", lon: -5.55, lat: 33.9, hub: true },
  { name: "Oujda", lon: -1.91, lat: 34.68, hub: true },
  { name: "Tetouan", lon: -5.37, lat: 35.57, hub: true },
  { name: "Al Hoceima", lon: -3.93, lat: 35.25, hub: true },
  { name: "El Jadida", lon: -8.5, lat: 33.25, hub: true },
  { name: "Beni Mellal", lon: -6.35, lat: 32.34, hub: true },
  { name: "Laayoune", lon: -13.2, lat: 27.15, hub: true },
  { name: "Constantine", lon: 6.61, lat: 36.36, hub: true },
  { name: "Annaba", lon: 7.77, lat: 36.9, hub: true },
  { name: "Setif", lon: 5.41, lat: 36.19, hub: true },
  { name: "Tlemcen", lon: -1.32, lat: 34.88, hub: true },
  { name: "Bejaia", lon: 5.08, lat: 36.75, hub: true },
  { name: "Sousse", lon: 10.64, lat: 35.83, hub: true },
  { name: "Monastir", lon: 10.83, lat: 35.78, hub: true },
  { name: "Gabes", lon: 10.1, lat: 33.88, hub: true },
  { name: "Bizerte", lon: 9.87, lat: 37.27, hub: true },
  { name: "Nouakchott", lon: -15.98, lat: 18.08, hub: true },
  { name: "Bamako", lon: -8.0, lat: 12.64, hub: true },
];

const ROUTE_COLORS = ["--route-blue", "--route-teal", "--route-green", "--route-violet", "--route-rose"];

const routes: [string, string][] = [
  ["Paris", "Casablanca"], ["Brussels", "Tangier"], ["Amsterdam", "Casablanca"], ["London", "Tangier"],
  ["Madrid", "Casablanca"], ["Barcelona", "Algiers"], ["Marseille", "Algiers"], ["Milan", "Tunis"],
  ["Berlin", "Tunis"], ["Copenhagen", "Tangier"], ["Stockholm", "Casablanca"], ["Paris", "Dakar"],
  ["Brussels", "Abidjan"], ["Marseille", "Douala"], ["Casablanca", "Dakar"], ["Dakar", "Abidjan"],
  ["Abidjan", "Douala"], ["Casablanca", "Abidjan"], ["Tunis", "Douala"],
  ["Rotterdam", "Nador"], ["Hamburg", "Casablanca"], ["Frankfurt", "Marrakech"], ["Munich", "Tunis"],
  ["Lyon", "Oran"], ["Zurich", "Algiers"], ["Rome", "Tunis"], ["Lisbon", "Agadir"], ["Bordeaux", "Fes"],
  ["Oslo", "Tangier"], ["Manchester", "Marrakech"], ["Malaga", "Tangier"], ["Paris", "Oran"],
  ["Brussels", "Nador"], ["Amsterdam", "Fes"], ["Berlin", "Agadir"], ["Milan", "Sfax"],
  ["Lyon", "Tunis"], ["Madrid", "Marrakech"], ["Barcelona", "Nador"], ["Marseille", "Sfax"],
  ["Frankfurt", "Algiers"], ["London", "Casablanca"], ["Copenhagen", "Agadir"], ["Paris", "Marrakech"],
  ["Rotterdam", "Tangier"], ["Hamburg", "Tunis"], ["Lisbon", "Dakar"], ["Madrid", "Dakar"],
  ["Rome", "Algiers"], ["Munich", "Oran"], ["Stockholm", "Tunis"], ["Brussels", "Casablanca"],
  ["Agadir", "Dakar"], ["Marrakech", "Abidjan"], ["Algiers", "Douala"],
  ["Paris", "Rabat"], ["Brussels", "Meknes"], ["Amsterdam", "Oujda"], ["Malaga", "Tetouan"],
  ["Rotterdam", "Al Hoceima"], ["Madrid", "El Jadida"], ["Milan", "Beni Mellal"], ["Lisbon", "Laayoune"],
  ["Marseille", "Constantine"], ["Lyon", "Annaba"], ["Paris", "Setif"], ["Barcelona", "Tlemcen"],
  ["Brussels", "Bejaia"], ["Frankfurt", "Sousse"], ["Rome", "Monastir"], ["Munich", "Gabes"],
  ["Zurich", "Bizerte"], ["London", "Rabat"], ["Berlin", "Oujda"], ["Hamburg", "Constantine"],
  ["Bordeaux", "Tetouan"], ["Manchester", "Fes"], ["Stockholm", "Sousse"], ["Copenhagen", "Annaba"],
  ["Madrid", "Nouakchott"], ["Paris", "Bamako"], ["Casablanca", "Nouakchott"], ["Laayoune", "Dakar"],
  ["Nouakchott", "Bamako"], ["Bamako", "Abidjan"], ["Oran", "Bamako"],
  ["Casablanca", "Oujda"], ["Tangier", "Oran"], ["Algiers", "Tunis"], ["Constantine", "Sfax"],
  ["Fes", "Tlemcen"], ["Rabat", "Marrakech"], ["Sousse", "Gabes"],
];

const city = (n: string) => cities.find((c) => c.name === n)!;

function arc(a: City, b: City, i: number) {
  const [x1, y1] = project(a.lon, a.lat);
  const [x2, y2] = project(b.lon, b.lat);
  const dx = x2 - x1;
  const dy = y2 - y1;
  const bend = (i % 2 ? -1 : 1) * (0.1 + (i % 5) * 0.035);
  const cx = (x1 + x2) / 2 - dy * bend;
  const cy = (y1 + y2) / 2 + dx * bend;
  return `M${x1.toFixed(1)},${y1.toFixed(1)} Q${cx.toFixed(1)},${cy.toFixed(1)} ${x2.toFixed(1)},${y2.toFixed(1)}`;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

const AnimatedRouteMap = () => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.15 });
  const reduced = usePrefersReducedMotion();
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [active, setActive] = useState(0);

  useEffect(() => {
    let alive = true;
    fetch("/maps/ne_50m_admin_0_countries.geojson")
      .then((r) => r.json())
      .then((json) => {
        const out: Shape[] = [];
        for (const f of json?.features || []) {
          const name = f?.properties?.NAME_EN || f?.properties?.NAME || "";
          const region: Region = EUROPE_COUNTRIES.has(name) ? "europe" : "other";
          const g = f?.geometry;
          const polys: number[][][][] = g?.type === "Polygon" ? [g.coordinates] : g?.type === "MultiPolygon" ? g.coordinates : [];
          for (const p of polys) {
            const ring = p?.[0];
            if (ring?.length && ringInFrame(ring)) out.push({ d: ringToPath(ring), region });
          }
        }
        if (alive) setShapes(out);
      })
      .catch(() => alive && setShapes([]));
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!isRevealed || reduced) return;
    const id = window.setInterval(() => setActive((a) => (a + 1) % routes.length), 1800);
    return () => window.clearInterval(id);
  }, [isRevealed, reduced]);

  const routePaths = useMemo(() => routes.map(([a, b], i) => arc(city(a), city(b), i)), []);
  const [activeFrom, activeTo] = routes[active];
  const activeColor = `hsl(var(${ROUTE_COLORS[active % ROUTE_COLORS.length]}))`;

  return (
    <section className="py-10 md:py-16 lg:py-20 overflow-hidden" style={{ background: "hsl(var(--navy))" }}>
      <style>{`
        @keyframes ecFlow { to { stroke-dashoffset: -40; } }
        .ec-flow { animation: ecFlow 1.6s linear infinite; }
        @keyframes ecFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .ec-fade { animation: ecFade .45s cubic-bezier(.16,1,.3,1); }
        @media (prefers-reduced-motion: reduce) { .ec-flow, .ec-fade { animation: none; } }
      `}</style>
      <div className="max-w-7xl mx-auto" ref={ref}>
        <div className="px-4 md:px-6">
          <div className="text-center lg:text-left">
            <span
              className="inline-flex border px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary"
              style={{ ...staggerStyle(0, isRevealed), borderColor: "hsl(var(--route-blue) / 0.3)", background: "hsl(var(--route-blue) / 0.08)" }}
            >
              {t("hero.routeMap.badge", "Our Network")}
            </span>
            <h2 className="font-display section-heading text-white mt-4 mb-3" style={staggerStyle(1, isRevealed)}>
              {t("hero.routeMap.heading", "Routes across Europe & Africa")}
            </h2>
            <p className="text-white/50 text-base md:text-lg leading-relaxed max-w-lg mx-auto lg:mx-0" style={staggerStyle(2, isRevealed)}>
              {t("hero.routeMap.subtitle", "Our verified couriers travel weekly between major European cities and Africa, ensuring your parcels arrive safely and on time.")}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8 max-w-5xl mx-auto" style={staggerStyle(4, isRevealed)}>
            {[
              { icon: Globe, title: t("hero.routeMap.feat1Title", "17 Countries"), desc: t("hero.routeMap.feat1Desc", "Africa ↔ Europe network"), color: "--route-blue" },
              { icon: ShieldCheck, title: t("hero.routeMap.feat2Title", "Verified Only"), desc: t("hero.routeMap.feat2Desc", "ID-checked couriers"), color: "--route-green" },
              { icon: Zap, title: t("hero.routeMap.feat3Title", "Book in 2 min"), desc: t("hero.routeMap.feat3Desc", "Instant quote & pay"), color: "--route-rose" },
              { icon: Activity, title: t("hero.routeMap.feat4Title", "Live Tracking"), desc: t("hero.routeMap.feat4Desc", "Real-time updates"), color: "--route-violet" },
            ].map((feat) => (
              <div key={feat.title} className="glass p-3 md:p-4 flex items-start gap-2.5 lg:gap-3">
                <div className="p-1.5 md:p-2 shrink-0" style={{ background: `hsl(var(${feat.color}) / 0.1)` }}>
                  <feat.icon className="h-4 w-4 md:h-5 md:w-5" style={{ color: `hsl(var(${feat.color}))` }} strokeWidth={1.8} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-bold text-xs md:text-sm text-white leading-tight">{feat.title}</div>
                  <div className="text-[10px] md:text-xs text-white/40 mt-0.5 leading-snug">{feat.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div
          className="relative mx-auto mt-6 md:mt-10 w-full max-w-4xl px-2 md:px-6"
          style={staggerStyle(3, isRevealed)}
          aria-label={t("hero.routeMap.heading", "Routes across Europe & Africa")}
        >
          <div className="relative border border-white/10 overflow-hidden" style={{ background: "hsl(var(--navy) / 0.6)" }}>
            <svg viewBox={`0 0 ${W} ${H}`} className="block w-full h-auto" xmlns="http://www.w3.org/2000/svg" role="img">
              <defs>
                <radialGradient id="ecSea" cx="45%" cy="72%" r="70%">
                  <stop offset="0%" stopColor="hsl(var(--route-teal))" stopOpacity="0.10" />
                  <stop offset="100%" stopColor="hsl(var(--route-blue))" stopOpacity="0" />
                </radialGradient>
                <pattern id="ecDots" width="16" height="16" patternUnits="userSpaceOnUse">
                  <circle cx="1" cy="1" r="1" fill="hsl(var(--primary-foreground) / 0.06)" />
                </pattern>
                <filter id="ecGlow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="4" result="b" />
                  <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              <rect width={W} height={H} fill="url(#ecDots)" />
              <rect width={W} height={H} fill="url(#ecSea)" />

              {shapes.map((s, i) => (
                <path
                  key={i}
                  d={s.d}
                  fill={
                    s.region === "europe"
                      ? "hsl(var(--route-blue) / 0.14)"
                      : "hsl(var(--primary-foreground) / 0.05)"
                  }
                  stroke={
                    s.region === "europe"
                      ? "hsl(var(--route-blue) / 0.35)"
                      : "hsl(var(--primary-foreground) / 0.1)"
                  }
                  strokeWidth={0.8}
                  strokeLinejoin="round"
                />
              ))}

              <text x={project(4, 38.6)[0]} y={project(4, 38.6)[1]} textAnchor="middle" fontSize="15" letterSpacing="6" fill="hsl(var(--primary-foreground) / 0.18)" fontStyle="italic">
                MEDITERRANEAN
              </text>

              {routePaths.map((d, i) => {
                const color = `hsl(var(${ROUTE_COLORS[i % ROUTE_COLORS.length]}))`;
                const isActive = i === active;
                return (
                  <g key={i} opacity={isRevealed ? 1 : 0} style={{ transition: `opacity .6s ease ${i * 40}ms` }}>
                    <path d={d} stroke={color} strokeOpacity={isActive ? 0.35 : 0.08} strokeWidth={isActive ? 6 : 1.5} fill="none" style={{ transition: "all .5s ease" }} />
                    <path
                      d={d}
                      className="ec-flow"
                      stroke={color}
                      strokeOpacity={isActive ? 1 : 0.4}
                      strokeWidth={isActive ? 2.6 : 1.1}
                      strokeDasharray="6 14"
                      strokeLinecap="round"
                      fill="none"
                      filter={isActive ? "url(#ecGlow)" : undefined}
                      style={{ transition: "stroke-opacity .5s ease, stroke-width .5s ease" }}
                    />
                    {isRevealed && !reduced && (
                      <circle r={isActive ? 5 : 3} fill={color} filter="url(#ecGlow)">
                        <animateMotion path={d} dur={`${3.2 + (i % 4) * 0.5}s`} begin={`${(i * 0.37).toFixed(2)}s`} repeatCount="indefinite" />
                      </circle>
                    )}
                    {isRevealed && !reduced && (
                      <circle r={2} fill={color} opacity={0.8}>
                        <animateMotion path={d} dur={`${4 + (i % 3) * 0.7}s`} begin={`${(i * 0.53 + 1.6).toFixed(2)}s`} repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear" />
                      </circle>
                    )}
                  </g>
                );
              })}

              {cities.map((c, i) => {
                const [x, y] = project(c.lon, c.lat);
                const on = activeFrom === c.name || activeTo === c.name;
                const anchor = c.anchor ?? "middle";
                const lx = anchor === "start" ? x + 11 : anchor === "end" ? x - 11 : x;
                const ly = c.dy ? y + c.dy : anchor === "middle" ? y - 14 : y + 5;
                return (
                  <g key={c.name} opacity={isRevealed ? 1 : 0} style={{ transition: `opacity .5s ease ${300 + i * 30}ms` }}>
                    <circle
                      cx={x}
                      cy={y}
                      r={on ? 6 : c.small ? 3 : 4}
                      fill="hsl(var(--navy))"
                      stroke={on ? activeColor : c.hub ? "hsl(var(--route-teal))" : "hsl(var(--route-blue))"}
                      strokeWidth={2}
                      style={{ transition: "r .4s ease" }}
                    />
                    {!c.hub && (!c.small || on) && <text
                      x={lx}
                      y={ly}
                      textAnchor={anchor}
                      fontSize={16}
                      fontWeight={on ? 700 : 500}
                      fill={on ? "hsl(var(--primary-foreground))" : "hsl(var(--primary-foreground) / 0.6)"}
                      paintOrder="stroke"
                      stroke="hsl(var(--navy))"
                      strokeWidth="4"
                      style={{ transition: "fill .4s ease" }}
                    >
                      {c.name}
                    </text>}
                  </g>
                );
              })}
            </svg>

            <div className="absolute left-3 top-3 md:left-5 md:top-5 glass px-3 py-2 md:px-4 md:py-3 pointer-events-none" aria-live="polite">
              <div className="flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-wider text-white/50">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping opacity-75" style={{ background: activeColor }} />
                  <span className="relative inline-flex h-2 w-2" style={{ background: activeColor }} />
                </span>
                {t("hero.routeMap.badge", "Our Network")}
              </div>
              <div key={active} className="ec-fade font-display font-bold text-sm md:text-lg text-white mt-1 whitespace-nowrap">
                {activeFrom} <span style={{ color: activeColor }}>→</span> {activeTo}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AnimatedRouteMap;
